// Einstiegspunkt. Registriert alle Systeme in Update-Reihenfolge und startet.
//
// Reihenfolge ist bedeutsam:
//   Zug vor Spieler  — der Spieler muss auf die schon aktualisierte Waggon-
//                      position aufsetzen, sonst zittert er beim Mitfahren.
//   Kamera zuletzt   — sie folgt fertigen Positionen (lateUpdate).

import { Engine } from './core/engine.js';
import { Input, InputFlush } from './core/input.js';
import { Connection } from './net/connection.js';

import { AssetSystem } from './render/assets.js';
import { SkySystem } from './world/sky.js';
import { TerrainSystem } from './world/terrain_mesh.js';
import { TrackSystem } from './world/track_mesh.js';
import { ScatterSystem } from './world/scatter.js';
import { CorridorSystem } from './world/corridor.js';
import { StationSystem } from './world/station.js';
import { CollisionSystem } from './world/collision.js';

import { TrainSystem } from './train/train.js';
import { PlayerSystem } from './player/player.js';
import { CameraSystem } from './player/camera.js';
import { RemotePlayersSystem } from './net/remote_players.js';

import { PaintSystem } from './paint/paint.js';
import { RadioSystem } from './radio/radio.js';
import { AudioSystem } from './audio/audio.js';
import { PhotoSystem } from './player/photo.js';
import { HudSystem, renderControls } from './ui/hud.js';
import { DebugSystem } from './ui/debug.js';

const canvas = document.getElementById('scene');
const titlecard = document.getElementById('titlecard');
const startButton = document.getElementById('start-button');
const loadingStatus = document.getElementById('loading-status');
const hud = document.getElementById('hud');
const reticle = document.getElementById('reticle');

// Tastenliste auf dem Startbildschirm: dieselbe Quelle wie das Overlay im Spiel.
renderControls(document.getElementById('title-keys'));

const engine = new Engine(canvas);
const input = new Input(canvas);
const net = new Connection();

engine.ctx.input = input;
engine.ctx.net = net;
engine.ctx.log = (msg) => {
  const sys = engine.get('hud');
  if (sys?.log) sys.log(msg);
  else console.log('[spiel]', msg);
};

// --- Systemregistrierung ----------------------------------------------------

// Muss zuerst laufen: Systeme, die Modelle benutzen, fragen in ihrem init()
// danach. Fehlt der assets-Ordner, bleibt die Bibliothek leer und alles baut
// weiter aus Code-Geometrie.
engine.add('assets', new AssetSystem());
engine.add('sky', new SkySystem());
engine.add('terrain', new TerrainSystem());
// Kollision vor track/corridor/station: die registrieren in init() Boxen.
// update() vor player: Arbeiter-Cache 1×/Frame, dann Substep-Auflösung.
engine.add('collision', new CollisionSystem());
engine.add('track', new TrackSystem());
engine.add('scatter', new ScatterSystem());
engine.add('corridor', new CorridorSystem());
engine.add('station', new StationSystem());
engine.add('train', new TrainSystem());
engine.add('paint', new PaintSystem());
engine.add('player', new PlayerSystem());
engine.add('remote', new RemotePlayersSystem());
engine.add('photo', new PhotoSystem());
engine.add('radio', new RadioSystem());
engine.add('audio', new AudioSystem());
engine.add('camera', new CameraSystem());
engine.add('hud', new HudSystem());
engine.add('debug', new DebugSystem());
engine.add('inputFlush', new InputFlush(input));

// --- Start ------------------------------------------------------------------

let ready = false;

net.on('status', (text) => {
  if (!ready) loadingStatus.textContent = text;
});

net.on('welcome', (msg) => {
  console.log(`[spiel] Season ${msg.season}, Session ${msg.id}`);
});

async function boot() {
  loadingStatus.textContent = 'baue Welt …';
  net.connect();

  await engine.initAll((name, i, total) => {
    loadingStatus.textContent = `baue Welt … ${name} (${i + 1}/${total})`;
  });

  // Ein paar Frames rendern, damit Shader kompiliert sind, bevor der Spieler
  // die Kontrolle bekommt — sonst ruckelt die erste Sekunde.
  engine.start();

  ready = true;
  loadingStatus.textContent = 'bereit';
  startButton.disabled = false;
  startButton.focus();
}

startButton.addEventListener('click', () => {
  titlecard.classList.add('gone');
  setTimeout(() => titlecard.classList.add('hidden'), 520);
  hud.classList.remove('hidden');
  reticle.classList.remove('hidden');
  input.requestLock();
  engine.ctx.started = true;
  engine.ctx.log('Du weißt nicht, wo du bist.');
});

// Klick auf die Szene fängt die Maus wieder ein, nachdem Esc sie freigegeben hat.
canvas.addEventListener('click', () => {
  if (engine.ctx.started && !input.locked && !input.textCapture) input.requestLock();
});

window.addEventListener('beforeunload', () => {
  engine.get('paint')?.flushPending?.();
});

boot().catch((err) => {
  console.error('[spiel] Start fehlgeschlagen:', err);
  loadingStatus.textContent = `Start fehlgeschlagen: ${err.message}`;
});

// Für die Konsole erreichbar machen — beim Bauen unbezahlbar.
window.__game = { engine, input, net, ctx: engine.ctx };
